import { EmptyResponseEntity } from "types/emptyResponseEntity";
import { type DocumentInstanceType, type TempFileTypes } from "types/enum";

import { APIHandler } from "utilities/apiHandler";

import { type BTLoginTypes } from "handlers";

import {
    type DeleteMediaRequestExternal,
    DocumentCreateResponse,
    FilesFromExternalUrlsResponse,
    type IDocumentCreateRequest,
    type IFilesFromExternalUrlsRequest,
    MetadataProductResponse,
    type NotifyOnDocumentLinkPermissionChangeRequest,
    type SetMainPhotoRequest,
    type UpdateDocumentViewPermissionsRequest,
} from "entity/document/Document/Document.api.types";

export interface IDocumentHandler {
    create(request: IDocumentCreateRequest): Promise<DocumentCreateResponse>;

    tempFilesFromExternalUrl(
        imageUrls: string[],
        jobId: number,
        tempFileType: TempFileTypes,
    ): Promise<FilesFromExternalUrlsResponse>;

    tempFilesFromExistingDocInstanceIds(
        ids: number[],
        jobId: number,
        tempFileType: TempFileTypes,
    ): Promise<FilesFromExternalUrlsResponse>;

    getMetadataForProducts(productUrls: string[]): Promise<MetadataProductResponse>;

    delete(
        documentType: DocumentInstanceType,
        documentInstanceIds: number[],
        builderId: number,
        entityId: number,
        jobId?: number,
    ): Promise<void>;

    externalDelete(shareToken: string, documentInstanceId: number): Promise<void>;

    toggleDocViewPermissions(
        documentInstanceId: number,
        loginType: BTLoginTypes,
        canView: boolean,
    ): Promise<EmptyResponseEntity>;

    notify(
        docInstanceId: number,
        loginType: BTLoginTypes,
        documentType: DocumentInstanceType,
    ): Promise<EmptyResponseEntity>;

    setMainPhoto(request: SetMainPhotoRequest): Promise<EmptyResponseEntity>;

    renameFile(documentInstanceId: number, title: string): Promise<EmptyResponseEntity>;
}

export class DocumentHandler implements IDocumentHandler {
    async create(request: IDocumentCreateRequest): Promise<DocumentCreateResponse> {
        return await APIHandler(`/api/Documents/FromTemplate`, {
            method: "POST",
            data: request,
            responseType: DocumentCreateResponse,
        });
    }

    async tempFilesFromExternalUrl(
        imageUrls: string[],
        jobId: number,
        tempFileType: TempFileTypes,
    ): Promise<FilesFromExternalUrlsResponse> {
        const request: IFilesFromExternalUrlsRequest = {
            externalUrls: imageUrls,
            jobId,
            tempFileType,
        };

        return await APIHandler("/api/Documents/TempFilesFromExternalUrl", {
            method: "POST",
            data: request,
            responseType: FilesFromExternalUrlsResponse,
        });
    }

    async tempFilesFromExistingDocInstanceIds(
        docInstanceIds: number[],
        jobId: number,
        tempFileType: TempFileTypes,
    ): Promise<FilesFromExternalUrlsResponse> {
        return await APIHandler("/api/Documents/TempFilesFromExistingDocs", {
            method: "POST",
            data: {
                docInstanceIds,
                jobId,
                tempFileType,
            },
            responseType: FilesFromExternalUrlsResponse,
        });
    }

    async getMetadataForProducts(productUrls: string[]): Promise<MetadataProductResponse> {
        return await APIHandler("/api/Documents/GetMetadataForProducts", {
            method: "POST",
            data: { productUrlList: productUrls },
            responseType: MetadataProductResponse,
        });
    }

    async delete(
        documentType: DocumentInstanceType,
        documentInstanceIds: number[],
        builderId: number,
        entityId: number,
        jobId?: number,
    ): Promise<void> {
        await APIHandler("/api/Documents/", {
            method: "DELETE",
            data: {
                attachedFiles: {
                    removeDocs: documentInstanceIds,
                },
                builderId,
                jobId,
                entityId,
                documentType,
            },
            responseType: EmptyResponseEntity,
        });
    }

    async externalDelete(shareToken: string, documentInstanceId: number): Promise<void> {
        const request: DeleteMediaRequestExternal = {
            shareToken,
            id: documentInstanceId,
        };
        await APIHandler("/api/Media/External", {
            method: "DELETE",
            data: request,
            responseType: EmptyResponseEntity,
        });
    }

    async toggleDocViewPermissions(
        documentInstanceId: number,
        loginType: BTLoginTypes,
        canView: boolean,
    ): Promise<EmptyResponseEntity> {
        const request: UpdateDocumentViewPermissionsRequest = {
            documentInstanceId,
            type: loginType,
            canView,
        };
        return await APIHandler(`/api/Documents/ToggleDocumentViewPermissions`, {
            method: "POST",
            data: request,
            responseType: EmptyResponseEntity,
        });
    }

    async notify(
        documentInstanceId: number,
        loginType: BTLoginTypes,
        documentType: DocumentInstanceType,
    ): Promise<EmptyResponseEntity> {
        const request: NotifyOnDocumentLinkPermissionChangeRequest = {
            documentInstanceId,
            type: loginType,
            documentType,
        };
        return await APIHandler(`/api/Documents/NotifyOnDocumentLinkPermissionChange`, {
            method: "PUT",
            data: request,
            responseType: EmptyResponseEntity,
        });
    }

    async setMainPhoto(request: SetMainPhotoRequest): Promise<EmptyResponseEntity> {
        return await APIHandler(`/api/Documents/MainPhoto`, {
            method: "POST",
            data: request,
            responseType: EmptyResponseEntity,
        });
    }

    async renameFile(documentInstanceId: number, title: string): Promise<EmptyResponseEntity> {
        return await APIHandler("/api/Documents/Rename", {
            method: "PUT",
            data: { documentInstanceId, title },
            responseType: EmptyResponseEntity,
        });
    }
}
